import { CHANNEL_INTERFACE_TYPE, ADVERTISER_SOURCE_TYPE } from '@/enums';
import { getNativeAnchorApi, pullNativeAnchorApi, batchCreateNativeAnchorApi } from '@/api/tool/native-anchor';
import { getSiteSelectorApi, pullSiteListApi, batchCreateSiteApi } from '@/api/tool/site';

/**
 * 页面配置
 * @typedef {Object} PageConfig
 * @property {string} channel_interface 渠道接口
 * @property {string} advertiser_source_type 资源类型
 * @property {Record<key, modelConfig>} model 表单/表格配置
 * @property {Record<key, modelConfig>} model.view_template_form_config 查看模板配置
 * @property {Object} model.view_template_form_config.form_config 表单项/差异化表单项配置
 * @property {Object} model.view_template_form_config.form_config 表单项/差异化表单项配置
 * @property {string} word_template 动态词包模板
 * @property {Object} rules 规则
 * @property {number} rules.min_length 标题最小长度
 * @property {number} rules.max_length 标题最大长度
 * @property {number} rules.max_count 标题最大个数
 * @property {number} rules.use_word_limit 允许使用词包总个数，为0则不限制
 * @property {Function} getUseWordCount 获取使用词包个数
 */

/**
 * 根据权限获取页面配置
 * @param {string} permission 页面权限值
 * @returns {PageConfig}
 */
export function getPageConfig(permission) {
  const base_key = 'batch-tool';
  const route_prefix = {
    NATIVE_ANCHOR: 'native-anchor',
    SITE: 'site',
  };
  const config = {
    // 原生锚点-头条
    [`${base_key}-${route_prefix.NATIVE_ANCHOR}-${CHANNEL_INTERFACE_TYPE.JRTT}`]: {
      channel_interface: CHANNEL_INTERFACE_TYPE.JRTT,
      advertiser_source_type: ADVERTISER_SOURCE_TYPE.NATIVE_ANCHOR,
      model: {
        view_template_form_config: {
          form_config: require('@/views/auxiliary/batch-tool/source-template/config/native-anchor/jrtt/model/view-template-form-config'),
          setDifferentiatedFields: (config) => {
            const {
              android_download_url,
              android_anchor_title,
              ios_download_url,
              ios_anchor_title,
            } = config.form_config;
            [
              android_download_url,
              android_anchor_title,
              ios_download_url,
              ios_anchor_title,
            ].forEach(form_item => {
              form_item.hidden = !form_item.value;
            });
          },
        },
        add_template_form_config: {
          form_config: require('@/views/auxiliary/batch-tool/source-template/config/native-anchor/jrtt/model/add-template-form-config'),
          setDifferentiatedFields: (config) => {
            const {
              advertiser_id,
              anchor_type,
              ...rest
            } = config.form_config;
            config.form_config = {
              advertiser_id,
              anchor_type,
              ...rest,
            };
          },
        },
        create_source_form_config: {
          form_config: require('@/views/auxiliary/batch-tool/source-template/config/native-anchor/jrtt/model/create-source-form-config'),
          setDifferentiatedFields: (config) => {
            const {
              name,
              advertiser_id,
              anchor_type,
              advertiser_source_name,
              ...rest
            } = config.form_config;
            advertiser_source_name.label = '锚点名称';
            config.form_config = {
              name,
              advertiser_id,
              anchor_type,
              advertiser_source_name,
              ...rest,
            };
          },
        },
      },
      getViewTemplateDifferentiatedFields: (params) => {
        const {
          jrtt_params: {
            anchor_type,
            game_type,
            platform_type,
            game_bonus,
            app_tags = [],
            ...rest
          } = {},
        } = params;

        // 查看模板枚举表
        const view_template_enum_map = {
        // 锚点类型
          anchor_type: {
            'APP_GAME': '应用下载-游戏',
          },
          // 推广内容
          game_type: {
            'GENERAL': '常规游戏下载',
            'MICRO_GAME': '微信小游戏',
          },
          // 配置平台
          platform_type: {
            1: '不限',
            2: '安卓',
            3: 'iOS',
          },
          // 游戏福利
          game_bonus: {
            'TRUE': '启用',
            'FALSE': '不启用',
          },
        };

        const payload = {
          anchor_type: view_template_enum_map.anchor_type[anchor_type] || '-',
          game_type: view_template_enum_map.game_type[game_type] || '-',
          platform_type: view_template_enum_map.platform_type[platform_type] || '-',
          game_bonus: view_template_enum_map.game_bonus[game_bonus] || '-',
          app_tags: app_tags.join('、'),
          ...rest,
        };

        return payload;
      },
      getAdvertiserSourceApi: getNativeAnchorApi,
      pullAdvertiserSourceApi: pullNativeAnchorApi,
      batchCreateApi: batchCreateNativeAnchorApi,
    },
    // 落地页-头条
    [`${base_key}-${route_prefix.SITE}-${CHANNEL_INTERFACE_TYPE.JRTT}`]: {
      channel_interface: CHANNEL_INTERFACE_TYPE.JRTT,
      advertiser_source_type: ADVERTISER_SOURCE_TYPE.SITE,
      components: {
        view_template: () => import('@/views/auxiliary/batch-tool/source-template/config/site/jrtt/components/view-template'),
      },
      model: {
        view_template_form_config: {
          form_config: require('@/views/auxiliary/batch-tool/source-template/config/site/jrtt/model/view-template-form-config'),
          setDifferentiatedFields: (config) => {
            const {
              android_download_link,
              ios_download_link,
            } = config.form_config;
            [
              android_download_link,
              ios_download_link,
            ].forEach(form_item => {
              form_item.hidden = !form_item.value;
            });
          },
        },
        create_source_form_config: {
          setDifferentiatedFields: (config) => {
            const { advertiser_source_name } = config.form_config;
            advertiser_source_name.label = '落地页名称';
          },
        },
      },
      getViewTemplateDifferentiatedFields: (params) => {
        const {
          jrtt_params: {
            thumbnail,
            ...rest
          } = {},
        } = params;

        const payload = {
          thumbnail: thumbnail ? [thumbnail] : [],
          ...rest,
        };

        return payload;
      },
      getAdvertiserSourceApi: getSiteSelectorApi,
      pullAdvertiserSourceApi: pullSiteListApi,
      batchCreateApi: batchCreateSiteApi,
    },
  };
  return config[permission];
}
